/**
 * Loss Runs row validation.
 *
 * Two modes, picked via `ValidateOptions.requireAll`:
 *
 *   • LIVE (default — what the table cells use as the user types):
 *     format-only, and only when the value is "complete-looking" so
 *     partial input doesn't flash errors mid-keystroke. A date errors
 *     once it reaches 10 chars (MM/DD/YYYY) and isn't a real date. The
 *     required-claimant check is skipped.
 *
 *   • STRICT (what `Save & Continue` runs through `lossRunsAreValid`):
 *     any non-empty invalid value flags AND required fields (Claimant)
 *     are checked. After the first failed save the page also passes
 *     this mode down to the cells so partial-typed dates are surfaced.
 *
 * Messages mirror the Figma / Henry's screenshots exactly so the
 * tooltip text matches what the design specs.
 *
 * Accident Year / Report Year are no longer validated here — they're
 * derived columns (`loss-run-derive.ts`), not user inputs.
 */
import type { LossRunRow } from "../../../types/loss-run";
import { isEmptyOrValidUsDate, isValidUsDate } from "../../../utils/date-validation";

export const REQUIRED_MESSAGE = "This is a required field. Complete it to proceed";
export const CLAIMANT_OR_CLAIM_MESSAGE = "Enter a Claimant Name or Claim # to save this row";
export const INVALID_DATE_MESSAGE = "The date format is incorrect. Please enter the date as MM/DD/YYYY";

export type LossRunField = "claimantName" | "incidentDate" | "reportDate" | "valued";
export type LossRunRowErrors = Partial<Record<LossRunField, string>>;

export interface ValidateOptions {
    /** Strict pass — see file header. Defaults to false (live). */
    requireAll?: boolean;
}

export function validateLossRunRow(row: LossRunRow, opts: ValidateOptions = {}): LossRunRowErrors {
    const errors: LossRunRowErrors = {};
    const strict = opts.requireAll === true;

    const dateInvalid = strict
        ? (v: string) => !isEmptyOrValidUsDate(v)
        : (v: string) => v.length === 10 && !isValidUsDate(v);

    if (dateInvalid(row.incidentDate)) errors.incidentDate = INVALID_DATE_MESSAGE;
    if (dateInvalid(row.reportDate)) errors.reportDate = INVALID_DATE_MESSAGE;
    if (dateInvalid(row.valued)) errors.valued = INVALID_DATE_MESSAGE;

    // A row is identifiable by EITHER a Claimant Name OR a Claim # (Linda,
    // jun-23) — only flag when both are missing.
    if (strict && row.claimantName.trim() === "" && row.claimNumber.trim() === "") errors.claimantName = CLAIMANT_OR_CLAIM_MESSAGE;

    return errors;
}

/** Save-time check — uses strict mode. */
export function lossRunsAreValid(rows: LossRunRow[]): boolean {
    return rows.every((row) => Object.keys(validateLossRunRow(row, { requireAll: true })).length === 0);
}
